import { ArrowLeftIcon, Button, Inline, Stack } from 'canoe';
import { useFormikContext } from 'formik';
import { ENDPOINTS, useActions } from 'hammock';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ContentPlacement, SelectorLocation } from '@candu-ai/placement/types';
import { SnapshotThumbnail } from 'craft/containers/Toolbox/tabs/Placements/components/snapshotThumbnail';
import { PlacementSteps, StepSectionTitle } from 'craft/containers/Toolbox/tabs/Placements/components/ui';
import { FormState } from 'craft/containers/Toolbox/tabs/Placements/constants';
import { removeSnapshot } from 'craft/containers/Toolbox/tabs/Placements/utils';
import {
  setForcePlacementsTab,
  setPreviewSelector,
  setPreviewSelectorLocation,
  setSnapshot,
  useToolboxTab,
} from 'craft/state';

import { FormStep1 } from './FormStep1';
import { FormStep2 } from './FormStep2';

enum PlacementFormStep {
  Selector = 'Selector',
  Location = 'Location',
}

interface StepTwoProps {
  onEditUrl: VoidFunction;
  handleRetargetClick: () => void;
  onGoBack?: VoidFunction;
}

export const FormSteps = ({ onEditUrl, onGoBack, handleRetargetClick }: StepTwoProps) => {
  const { values, setFieldValue, submitForm } = useFormikContext<ContentPlacement>();
  const { forcePlacementsTab } = useToolboxTab();
  const dispatch = useDispatch();
  const { delete: deleteSnapshot } = useActions(ENDPOINTS.snapshots);

  const [state] = useState(values.id ? FormState.UPDATE : FormState.CREATE);
  const [activeStep, setActiveStep] = useState(
    state === FormState.UPDATE ? undefined : PlacementFormStep.Location,
  );

  const [snapshotIdToDelete, setSnapshotIdToDelete] = useState<string | undefined>();

  useEffect(() => {
    // make sure that the editor is locked when editing a placement, but not when previewing it or if there is no snapshot
    if (!activeStep && forcePlacementsTab) {
      dispatch(setForcePlacementsTab(false));
    } else if (activeStep && !forcePlacementsTab) {
      dispatch(setForcePlacementsTab(true));
    }
  }, [activeStep, forcePlacementsTab]);

  useEffect(() => {
    setSnapshotIdToDelete(undefined);
  }, [values.id]);

  const onApplyChanges = useCallback(
    (nextStep: VoidFunction) => {
      submitForm().then(nextStep);

      if (snapshotIdToDelete) {
        removeSnapshot({
          deleteSnapshot,
          snapshotId: snapshotIdToDelete,
          contentId: values.contentId,
          hideNotification: true,
        });
      }
    },
    [snapshotIdToDelete, submitForm, removeSnapshot, onGoBack],
  );

  return (
    <PlacementSteps>
      {onGoBack && (
        <Stack>
          <Button
            onClick={onGoBack}
            variant="white"
            appearance="text"
            inline
            size="xsmall"
            disabled={forcePlacementsTab}
          >
            <Inline space="xxs">
              <ArrowLeftIcon color="White" size="s" />
              Back
            </Inline>
          </Button>
        </Stack>
      )}
      <Stack>
        <FormStep1
          active={activeStep === PlacementFormStep.Selector}
          state={state}
          onEditUrl={onEditUrl}
          onEditSelector={() => {
            setActiveStep(PlacementFormStep.Selector);
          }}
          handleRetargetClick={handleRetargetClick}
          onDeletePlacementSnapshot={() => {
            setSnapshotIdToDelete(values.snapshotId);
            setFieldValue('snapshotId', undefined);

            // clear snapshot preview from editor
            dispatch(setSnapshot(undefined, undefined));
          }}
          onSave={(value: string) => {
            setFieldValue('selector', value, false);

            if (state === FormState.UPDATE) {
              onApplyChanges(() => {
                setActiveStep(undefined);
                dispatch(setPreviewSelector(undefined));
              });
            } else {
              setActiveStep(PlacementFormStep.Location);
              dispatch(setPreviewSelector(value));
            }
          }}
          onCancel={() => {
            if (state === FormState.UPDATE) {
              if (snapshotIdToDelete) {
                onApplyChanges(() => {
                  setActiveStep(undefined);
                  dispatch(setPreviewSelector(undefined));
                });
              } else {
                setActiveStep(undefined);
                dispatch(setPreviewSelector(undefined));
              }
            } else {
              setActiveStep(PlacementFormStep.Location);
              dispatch(setPreviewSelector(values.selector));
            }
          }}
        />

        <FormStep2
          active={activeStep === PlacementFormStep.Location}
          state={state}
          onSave={(value: SelectorLocation) => {
            setFieldValue('location', value, false);

            onApplyChanges(() => {
              setActiveStep(undefined);
              dispatch(setPreviewSelector(undefined));
              dispatch(setPreviewSelectorLocation(undefined));
            });
          }}
          onEdit={() => {
            setActiveStep(PlacementFormStep.Location);
          }}
          onCancel={() => {
            setActiveStep(undefined);
            dispatch(setPreviewSelectorLocation(undefined));
          }}
        />

        <StepSectionTitle size="short" active>
          Preview Snapshot
        </StepSectionTitle>
        <SnapshotThumbnail contentId={values.contentId} snapshotId={values.snapshotId} />
      </Stack>
    </PlacementSteps>
  );
};
